import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { ConfirmDialog, MdiIcon, Table, useSnackbar } from '@components';
import { mdiCheck, mdiClose, mdiMagnify, mdiPlus } from '@mdi/js';
import moment from 'moment';
import { FimCotacaoDialog } from './fim-cotacao';
import { useModal } from 'mui-modal-provider';
const CotacoesList = ({ $state, CrmService, bloqueado, client, contacto, funcionario, vendedor }) => {
    // Tabela
    const loadParams = () => {
        const sortBy = $state.params.sortBy;
        if (sortBy == undefined) {
            return [];
        }
        const o = [];
        sortBy.forEach(r => {
            const s = r.split(':');
            o.push({ id: s[0], desc: s[1] == 'DESC' });
        });
        return o;
    };
    const [data, setData] = useState(); // dados dos Contactos
    const [loading, setLoading] = useState(true);
    const [refetching, setRefetching] = useState(false);
    const [error, setError] = useState(false);
    const [filter, setFilter] = useState([]);
    const [sort, setSort] = useState(() => loadParams());
    const [options, setOptions] = useState({
        filter: [],
        sort: []
    });
    const [pagination, setPagination] = useState({
        pageIndex: $state.params.page - 1 || 0,
        pageSize: $state.params.limit || 10
    });
    const { showModal } = useModal();
    const { showSnackbar } = useSnackbar();
    const [estados, setEstados] = useState([]); // Estados das cotações
    const getType = (type) => {
        switch (type) {
            case 'contains':
                return '$ilike';
            case 'startsWith':
                return '$sw';
            case 'notEquals':
                return '$not';
            case 'empty':
                return '$null';
            case 'notEmpty':
                return '$notNull';
            case 'greaterThan':
                return '$gt';
            case 'greaterThanOrEqualTo':
                return '$gte';
            case 'lessThan':
                return '$lt';
            case 'lessThanOrEqualTo':
                return '$lte';
            default:
                return '$eq';
        }
    };
    const columns = useMemo(() => [
        {
            accessorKey: 'numeroCotacao',
            header: 'Nº Cotação',
            size: 100
        },
        {
            accessorKey: 'dataAbertura',
            header: 'Data de Abertura',
            size: 90,
            Cell: ({ row }) => {
                if (row.original?.dataAbertura) {
                    let d = moment(row.original.dataAbertura);
                    if (d.isValid())
                        return d.format('DD/MM/YYYY HH:mm');
                }
                return 'N/D';
            }
        },
        {
            accessorKey: 'CoreCliente.nome',
            header: 'Cliente',
            size: 150
        },
        {
            accessorKey: 'titulo',
            header: 'Título',
            size: 150
        },
        {
            accessorKey: 'criadoPor',
            header: 'Criado por',
            size: 150
        },
        {
            accessorKey: 'Funcionario.name',
            header: 'Responsável Atual',
            size: 150
        },
        {
            accessorKey: 'Estado.id',
            exportKey: 'Estado.designacao',
            header: 'Estado',
            size: 150,
            filterSelectOptions: estados.map(r => {
                return {
                    label: r.designacao,
                    value: r.id
                };
            }),
            Cell: ({ row }) => row.original?.Estado?.designacao
        },
        {
            accessorKey: 'adjudicada',
            header: 'Proposta?',
            size: 120,
            Cell: ({ row }) => {
                if (row.original?.adjudicada != null) {
                    return row.original.adjudicada ? 'Sim' : 'Não';
                }
                else {
                    if (row.original?.estadoId === 70)
                        return 'Aguarda';
                }
                return 'N/D';
            }
        },
        {
            accessorKey: 'dataEmCotacao',
            header: 'Data em Cotação',
            size: 90,
            Cell: ({ row }) => {
                if (row.original?.dataEmCotacao) {
                    let d = moment(row.original.dataEmCotacao);
                    if (d.isValid())
                        return d.format('DD/MM/YYYY HH:mm');
                }
                return 'N/D';
            }
        },
        {
            accessorKey: 'total',
            header: 'Total €',
            size: 100,
            muiTableBodyCellProps: {
                align: 'right'
            },
            Cell: ({ row }) => (row.original.total ? row.original.total.toString().replace('.', ',') + ' €' : '-')
        },
        {
            accessorKey: 'totalComDesconto',
            header: 'Total C/ Desconto €',
            size: 120,
            muiTableBodyCellProps: {
                align: 'right'
            },
            Cell: ({ row }) => (row.original.totalComDesconto ? row.original.totalComDesconto.toString().replace('.', ',') + ' €' : '-')
        },
        {
            accessorKey: 'dataConclusao',
            header: 'Data de Conclusão',
            size: 90,
            Cell: ({ row }) => {
                if (row.original?.dataConclusao) {
                    let d = moment(row.original.dataConclusao);
                    if (d.isValid())
                        return d.format('DD/MM/YYYY HH:mm');
                }
                return 'N/D';
            }
        }
    ], [estados]);
    // ir para detalhes da pessoa (envia id da empresa)
    const onCellView = (cell) => {
        $state.go('app.crm.cotacoes.formEdit', { idEmpresa: client, id: cell.id });
    };
    const initialFetch = async () => {
        if (loading) {
            setLoading(false);
            const estadosCotacao = await CrmService.getEstadosCotacao();
            setEstados(estadosCotacao);
            const opt = await CrmService.optionsCotacoes();
            setOptions(opt);
        }
    };
    const fetchData = async () => {
        setError(false);
        try {
            let filters = {};
            filter.forEach(r => {
                filters[r.id] = `${getType(r.type)}:${r.value}`;
            });
            let sorter = [];
            sort.forEach(r => {
                sorter.push(`${r.id}:${r.desc ? 'DESC' : 'ASC'}`);
            });
            if (client !== undefined) {
                filters['clienteId'] = `$eq:${client}`;
            }
            if (contacto !== undefined) {
                filters['contactoId'] = `$eq:${contacto}`;
            }
            if (funcionario !== undefined) {
                filters['funcionarioId'] = `$eq:${funcionario}`;
            }
            if (vendedor !== undefined) {
                filters['funcionarioId'] = `$eq:${vendedor}`;
            }
            const aux = await CrmService.findAllCotacoes({
                limit: pagination.pageSize,
                page: pagination.pageIndex + 1,
                filter: filters,
                sortBy: sorter
            });
            $state.go($state.current.name, {
                limit: pagination.pageSize,
                page: pagination.pageIndex + 1,
                sortBy: sorter,
                filter: Object.keys(filter).length > 0 ? JSON.stringify(filters) : undefined
            }, {
                notify: false,
                reload: false,
                location: 'replace',
                inherit: true
            });
            // Update URL
            setData(aux);
        }
        catch (e) {
            console.log(e);
            setError(true);
        }
        setRefetching(false);
    };
    useEffect(() => {
        initialFetch();
        if (!refetching) {
            setRefetching(true);
            fetchData();
        }
    }, [pagination, filter, sort]);
    return (<Box sx={{ mt: 2 }}>
      <Stack direction='column'>
        <Stack direction='row' justifyContent='flex-start' alignItems='center'>
          <Typography variant='h6' sx={{ color: 'black' }}>
            Cotações
          </Typography>

          {client && !bloqueado ? (<Tooltip title={'Adicionar Cotação'}>
              <IconButton color='primary' onClick={() => $state.go('app.crm.cotacoes.formCreate', { idEmpresa: client })}>
                <MdiIcon path={mdiPlus} sx={{ color: '#1976D2' }}></MdiIcon>
              </IconButton>
            </Tooltip>) : (<></>)}
        </Stack>

        <Box sx={{
            borderRadius: '15px', // Aplica o arredondamento na parte externa
            border: '1px solid #e0e0e0', // Adiciona uma borda leve
            overflow: 'hidden', // Garante que o conteúdo não extrapole o arredondamento
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)' // Adiciona uma leve sombra
        }}>
          <Table onDoubleClick={row => {
            onCellView(row);
        }} storageKey={$state.current.name} data={data} columns={columns} initial={{
            sort: sort,
            filter: filter
        }} isLoading={refetching} options={options} onPaginate={setPagination} onFilter={setFilter} onSort={setSort} rowActionSize={150} actionItems={({ row }) => [
            <Box key={`actions-${row.original.id}`} gap={1} sx={{
                    width: '100%', // Ocupa toda a largura da célula
                    height: '100%', // Ocupa toda a altura da célula
                    display: 'flex',
                    justifyContent: 'center', // Centra horizontalmente
                    alignItems: 'center' // Centra verticalmente
                }}>
                <Tooltip title={'Ver detalhes'} placement='top'>
                  <IconButton color='secondary' onClick={() => {
                    $state.go('app.crm.cotacoes.formEdit', { idEmpresa: row.original.clienteId, id: row.original.id });
                }}>
                    <MdiIcon path={mdiMagnify} sx={{ color: '#1976D2' }}/>
                  </IconButton>
                </Tooltip>
                {row.original.estadoId == 70 && row.original.adjudicada == null ? (<>
                    <Tooltip title={'Associar proposta'} placement='top'>
                      <IconButton color='success' onClick={() => {
                        showModal(FimCotacaoDialog, {
                            title: 'Finalizar cotação',
                            message: 'De modo a marcar a cotação como adjudicada, introduza o número do documento da proposta.',
                            clienteId: row.original.clienteId,
                            service: CrmService,
                            onClose: async (result) => {
                                if (result) {
                                    // TODO: Marcar cotação como adjudicada
                                    let ok = false;
                                    try {
                                        await CrmService.adjudicaCotacao(row.original.id, result);
                                        ok = true;
                                    }
                                    catch (e) {
                                        console.log(e);
                                    }
                                    showSnackbar(ok ? 'Proposta associada a cotação' : 'Erro ao adjudicar cotação');
                                    fetchData();
                                }
                            }
                        });
                    }}>
                        <MdiIcon path={mdiCheck}/>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={'Marcar como Não Adjudicada'} placement='top'>
                      <IconButton color='error' onClick={() => {
                        showModal(ConfirmDialog, {
                            title: 'Finalizar cotação',
                            message: `Tem a certeza que pretende marcar a cotação ${row.original.numeroCotacao} como não adjudicada? Esta ação não pode ser revertida!`,
                            onClose: async (result) => {
                                if (result) {
                                    await CrmService.patchCotacao(row.original.id, {
                                        adjudicada: false
                                    });
                                    showSnackbar('Cotação marcada como não adjudicada');
                                    fetchData();
                                }
                            }
                        });
                    }}>
                        <MdiIcon path={mdiClose}/>
                      </IconButton>
                    </Tooltip>
                  </>) : (<></>)}
              </Box>
        ]}></Table>
        </Box>
      </Stack>
    </Box>);
};
export default CotacoesList;
